import { ComponentType } from '@wix/ambassador-app-service-webapp/http';
import {
  AppData,
  DocumentServicesObject,
  UnifiedWidget,
} from '@wix/document-services-types';

const TEST_VERSION = 'latest';

interface AppDataWithVersions extends AppData {
  latestVersion?: string;
  blocksVersion?: string;
}

export const isAppUpdateAvailable = (
  documentServices: DocumentServicesObject,
  appDefId: string,
  latestVersion?: string,
) => {
  const appData = documentServices.platform.getAppDataByAppDefId(appDefId);

  if (!appData) {
    throw new Error(`Application not installed: ${appDefId}`);
  }
  if (!appData.appFields?.semanticVersion && !appData.version) {
    return false;
  }
  if (appData?.appFields?.installedVersion === TEST_VERSION) {
    return false;
  }

  const app: AppDataWithVersions = {
    ...appData,
    latestVersion,
    blocksVersion: getBlocksVersion(appData),
  };

  if (app.blocksVersion) {
    // TODO because we do not open Consent Modal on update
    // TODO there are issues with permissions on blocks - so we filter them out
    // TODO https://wix.slack.com/archives/C08CZHHH2R1/p1739374970581809
    return false;
  }

  const installedSemVer = getVersion(
    app.appFields?.semanticVersion || app.version,
  );

  // if we don't have a latest version assume the installed version is the latest.
  const latestSemVer = app.latestVersion
    ? getVersion(app.latestVersion)
    : installedSemVer;

  if (installedSemVer.major !== latestSemVer.major) {
    return true;
  }

  return isBlocksUpdateAvailable(app);
};

const getBlocksVersion = (appData: AppData) => {
  const studioWidgetComponent = appData?.components?.find(
    ({ type }) => type === ComponentType.STUDIO_WIDGET,
  );

  if (!studioWidgetComponent) {
    // only Responsive Blocks (2.0.0) supports apps with CODE_PACKAGE or BACK_OFFICE_PAGE component
    const isResponsiveBlocks = appData?.components?.some(
      ({ type }) =>
        type &&
        [ComponentType.CODE_PACKAGE, ComponentType.BACK_OFFICE_PAGE].includes(
          type as ComponentType,
        ),
    );

    if (isResponsiveBlocks) {
      return '2.0.0';
    }
  }

  return (studioWidgetComponent?.data as UnifiedWidget)?.blocksVersion;
};

const getVersion = (version?: string) => {
  if (!version) {
    return {
      major: 0,
      minor: 0,
    };
  }

  const cleanVersion = version.replace(/^\^/, '');

  const splitVersion = cleanVersion.split('.').map((v) => parseInt(v, 10));
  return {
    major: splitVersion[0],
    minor: splitVersion[1],
  };
};

const isBlocksUpdateAvailable = (app: AppDataWithVersions) => {
  const isResponsiveBlocksVersion = app.blocksVersion?.startsWith('2.');
  if (!isResponsiveBlocksVersion) {
    return false;
  }

  const installedBlocksVersion = app?.appFields?.installedVersion;
  if (!installedBlocksVersion) {
    return false;
  }

  const isAutoMinorUpdatesOn =
    app?.appFields?.installedVersion?.substring(0, 1) === '^';

  const hasMinorVersionUpdate =
    getVersion(app.latestVersion)?.minor !==
    getVersion(installedBlocksVersion)?.minor;

  return !isAutoMinorUpdatesOn && hasMinorVersionUpdate;
};
